import React from 'react';

import {
  SearchClient,
  SearchParamsObject,
  SearchResponse,
  searchClient,
} from '@algolia/client-search';
import Constants from '@helpers/Constants';
import { AppQueryDomain, createQueryKey, nullableQueryKeyPart, useAppQuery } from '@helpers/query';
import { GroupSearchHit, GroupSearchResult, UserSearchHit, UserSearchResult } from '@types';

export type SearchProps<Hit, Result> = {
  query: string;
  indexName: string;
  includedObjectIds?: readonly string[];
  excludedObjectIds?: readonly string[];
  responseToResult: (response: SearchResponse<Hit>) => Result[];
};

export type ObjectIdSearchFilter = {
  filters?: string;
  hasNoMatches: boolean;
};

function useSearch<Hit, Result>({
  query,
  indexName,
  includedObjectIds,
  excludedObjectIds,
  responseToResult,
}: SearchProps<Hit, Result>) {
  const objectIdSearchFilter = React.useMemo(
    () => buildObjectIdSearchFilter(includedObjectIds, excludedObjectIds),
    [excludedObjectIds, includedObjectIds]
  );

  const client = React.useMemo(() => {
    const { appId, apiKey } = Constants.joinstar.algolia;
    return searchClient(appId, apiKey);
  }, []);
  const enabled = query.length > 0 && !objectIdSearchFilter.hasNoMatches;
  const searchQuery = useAppQuery({
    queryKey: createQueryKey(
      AppQueryDomain.ALGOLIA,
      indexName,
      query,
      nullableQueryKeyPart(objectIdSearchFilter.filters)
    ),
    queryFn: async () =>
      runQuery(client, indexName, query, responseToResult, objectIdSearchFilter.filters),
    enabled,
    placeholderData: [],
  });

  return {
    results: enabled ? (searchQuery.data ?? []) : [],
    loading: searchQuery.isFetching,
    error: searchQuery.isError,
  };
}

async function runQuery<Hit, Result>(
  client: SearchClient,
  indexName: string,
  query: string,
  responseToResult: (response: SearchResponse<Hit>) => Result[],
  filters?: string
) {
  const searchParams: SearchParamsObject = filters ? { filters, query } : { query };
  const response = await client.searchSingleIndex<Hit>({ indexName, searchParams });

  return responseToResult(response);
}

export function buildObjectIdSearchFilter(
  includedObjectIds?: readonly string[],
  excludedObjectIds?: readonly string[]
): ObjectIdSearchFilter {
  if (!includedObjectIds && !excludedObjectIds) {
    return { hasNoMatches: false };
  }

  const uniqueObjectIds = includedObjectIds ? getUniqueObjectIds(includedObjectIds) : undefined;
  if (includedObjectIds && uniqueObjectIds?.length === 0) {
    return { hasNoMatches: true };
  }
  const uniqueExcludedObjectIds = excludedObjectIds
    ? getUniqueObjectIds(excludedObjectIds)
    : undefined;

  const filters = [
    uniqueObjectIds ? buildIncludedObjectIdFilter(uniqueObjectIds) : undefined,
    ...(uniqueExcludedObjectIds || []).map(
      (objectId) => `NOT objectID:${formatAlgoliaFilterValue(objectId)}`
    ),
  ].filter((filter) => filter !== undefined);

  return {
    filters: filters.length > 0 ? filters.join(' AND ') : undefined,
    hasNoMatches: false,
  };
}

function getUniqueObjectIds(objectIds: readonly string[]) {
  return [...new Set(objectIds.filter((objectId) => objectId.length > 0))];
}

function buildIncludedObjectIdFilter(objectIds: string[]) {
  const filters = objectIds
    .map((objectId) => `objectID:${formatAlgoliaFilterValue(objectId)}`)
    .join(' OR ');
  return objectIds.length > 1 ? `(${filters})` : filters;
}

function formatAlgoliaFilterValue(value: string) {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

export function useUserSearch({
  query,
  includedUserIds,
  excludedUserIds,
}: {
  query: string;
  includedUserIds?: readonly string[];
  excludedUserIds?: readonly string[];
}) {
  return useSearch({
    query,
    indexName: 'users',
    includedObjectIds: includedUserIds,
    excludedObjectIds: excludedUserIds,
    responseToResult: formatUserSearchResponse,
  });
}

export function useGroupSearch({
  query,
  includedGroupIds,
  excludedGroupIds,
}: {
  query: string;
  includedGroupIds?: readonly string[];
  excludedGroupIds?: readonly string[];
}) {
  return useSearch({
    query,
    indexName: 'groups',
    includedObjectIds: includedGroupIds,
    excludedObjectIds: excludedGroupIds,
    responseToResult: formatGroupSearchResponse,
  });
}

export function formatUserSearchResponse(
  response: Pick<SearchResponse<UserSearchHit>, 'hits'>
): UserSearchResult[] {
  return response.hits.map((hit) => ({
    userId: hit.objectID,
    username: hit.username,
    displayName: hit.displayName,
    photoUrl: hit.photoUrl,
    hometown: {
      displayName: hit['hometown.displayName'],
    },
    numFollowers: hit.numFollowers || 0,
  }));
}

export function formatGroupSearchResponse(
  response: Pick<SearchResponse<GroupSearchHit>, 'hits'>
): GroupSearchResult[] {
  return response.hits.map((hit) => ({
    groupId: hit.objectID,
    displayName: hit.displayName,
    description: hit.description,
    photoUrl: hit.photoUrl,
    verified: hit.verified,
    numMembers: hit.numMembers,
  }));
}
